import { Icon, IconProps, Text, TextProps } from "@chakra-ui/react"
import { Button, ButtonProps } from "@chakra-ui/react/button"
import { LinkProps } from "@chakra-ui/react/link"
import * as React from "react"

import NextLink from "@/components/RouteTransitionLink"

type ButtonLinkBaseProps = Omit<LinkProps & ButtonProps, "children"> & {
  href: string
  leadingIcon?: React.ReactNode
  leadingIconProps?: IconProps
  nextLinkProps?: Omit<
    React.ComponentProps<typeof NextLink>,
    "children" | "href" | "target"
  >
  trailingIcon?: React.ReactNode
  trailingIconProps?: IconProps
  secondary?: boolean
  fullWidth?: boolean
  target?: string
}

type ButtonLinkContentProps =
  | {
      children?: never
      label: string
      TextProps?: TextProps
    }
  | {
      children: React.ReactNode
      label?: never
      TextProps?: never
    }

type ButtonLinkProps = ButtonLinkBaseProps & ButtonLinkContentProps

const renderIconSlot = (icon: React.ReactNode, iconProps?: IconProps) => {
  if (!icon) {
    return null
  }

  if (React.isValidElement(icon)) {
    return React.cloneElement(
      icon as React.ReactElement<Record<string, unknown>>,
      iconProps as Partial<Record<string, unknown>>
    )
  }

  return <Icon as={icon as React.ElementType} {...iconProps} />
}

const ButtonLink = ({
  children,
  fullWidth = false,
  href,
  label,
  leadingIcon,
  leadingIconProps,
  nextLinkProps,
  secondary = false,
  target = "_self",
  TextProps,
  trailingIcon,
  trailingIconProps,
  ...props
}: ButtonLinkProps) => {
  const shouldUseRouteTransition =
    target === "_self" && href.startsWith("/") && !href.startsWith("//")
  const content = children ?? (
    <Text as="span" {...TextProps}>
      {label}
    </Text>
  )

  return (
    <Button
      _hover={{
        transform: "scale(1.05)",
      }}
      alignItems="center"
      appearance="none"
      aria-label={label}
      asChild
      borderColor="transparent"
      borderWidth="1px"
      colorPalette={"primary"}
      cursor="pointer"
      display={"inline-flex"}
      flexShrink="0"
      fontFamily={"body"}
      fontSize="sm"
      fontWeight="medium"
      gap="2"
      height="10"
      isolation="isolate"
      justifyContent="center"
      lineHeight="1.25rem"
      outline="0"
      paddingInline="4"
      position="relative"
      rounded="full"
      textDecoration={"none"}
      transitionDuration="moderate"
      transitionProperty="background-color, border-color, color, fill, stroke, opacity, box-shadow, translate, transform"
      userSelect="none"
      verticalAlign="middle"
      whiteSpace="nowrap"
      width={fullWidth ? "100%" : "auto"}
      zIndex={"3"}
      {...props}
    >
      <NextLink
        href={href}
        passHref
        target={target}
        transitionTypes={shouldUseRouteTransition ? ["nav-forward"] : undefined}
        {...nextLinkProps}
      >
        {renderIconSlot(leadingIcon, leadingIconProps)}
        {content}
        {renderIconSlot(trailingIcon, trailingIconProps)}
      </NextLink>
    </Button>
  )
}

export default ButtonLink
